import React from "react";
import { Grid, TextField, MenuItem, InputAdornment } from "@mui/material";
import { Voltage } from "../../models/signalUsers/voltage.ts";
import { Signal } from "../../models/signal.ts";
import { getSignalLabelFromList } from "../../utils/signalUtils.ts";
import { useResistorOptions } from "../../hooks/useResistorOptions.ts"

interface Props {
  data: Voltage;
  setIsValid: (isValid: boolean) => void;
  onChange: (voltage: Voltage) => void;
  signalOptions: Signal[];
}

const EditVoltageForm: React.FC<Props> = ({ data, setIsValid, onChange, signalOptions }) => {
    const { options } = useResistorOptions();
    const maxVoltageMin = 4;

    const handleChange = (voltage: Voltage) => {
        voltage.pull_up_resistor = calculatePullUp(voltage.pull_down_resistor, voltage.max);
        voltage.resolution = calculateResolution(voltage.pull_up_resistor, voltage.pull_down_resistor, voltage.max);
        voltage.load = calculateLoad(voltage.pull_up_resistor, voltage.pull_down_resistor, voltage.max)
        onChange(voltage);
        setIsValid(voltage.signal.id !== "" && voltage.max > maxVoltageMin);
    };


    const calculatePullUp = (pullDown: number, maxVoltage: number): number => {
        const vrefAdc = 3.0;
        let pullUpInTheory = (maxVoltage / vrefAdc - 1) * pullDown
        const minResistor = (maxVoltage - 1.5) * pullDown / 1.5;
        if (pullUpInTheory < minResistor) { 
            pullUpInTheory = minResistor;
        }
        const resistors = options.sort((a, b) => a - b);
        const pullUp = resistors.find(r => r >= pullUpInTheory);
        return pullUp ?? resistors[resistors.length - 1];
    };

    const calculateResolution = (pullUp: number, pullDown: number, maxVoltage: number) => {
        const adcResolution = 65536;
        const voltageDividerRatio = pullDown / (pullDown + pullUp);
        const measuredVoltage = maxVoltage * voltageDividerRatio;
        return measuredVoltage / adcResolution;
    };

    const calculateLoad = (pullUp: number, pullDown: number, maxVoltage: number): number => {
        return maxVoltage / (pullUp + pullDown);
    };

    return (
        <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid size={{ xs: 12, sm: 12 }}>
                <TextField
                select fullWidth label="Signal"
                value={data.signal.id}
                error={data.signal.id === ""}
                onChange={(e) => {
                    const selected = signalOptions.find(s => s.id === e.target.value);
                    if (selected) handleChange({ ...data, signal: selected });
                }}
                >
                {signalOptions.map(opt => (
                    <MenuItem key={opt.id} value={opt.id}>{getSignalLabelFromList(opt.id, signalOptions)}</MenuItem>
                ))}
                </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 12 }}>
                <TextField
                label="Max. Spannung"
                type="number"
                fullWidth
                inputProps={{ step: 0.01, min: maxVoltageMin }}
                value={data.max}
                error={data.max <= maxVoltageMin}
                onChange={(e) => handleChange({ ...data, max: parseFloat(e.target.value) })}
                InputProps={{
                    endAdornment: <InputAdornment position="end">V</InputAdornment>,
                }}
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                    label="Pull-up-Widerstand"
                    value={data.pull_up_resistor.toFixed(0)}
                    fullWidth
                    variant="filled"
                    disabled
                    InputProps={{ 
                        readOnly: true,
                        endAdornment: <InputAdornment position="end">Ω</InputAdornment>,
                    }}
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                    select
                    label="Pull-down-Widerstand"
                    value={data.pull_down_resistor.toFixed(0)}
                    onChange={(e) => handleChange({ ...data, pull_down_resistor: parseInt(e.target.value, 10) })}
                    fullWidth
                    variant="filled"
                    SelectProps={{
                        IconComponent: () => null,
                    }}
                    InputProps={{ 
                        endAdornment: <InputAdornment position="end">Ω</InputAdornment>,
                    }}
                >
                    <MenuItem value={10000}>10 000</MenuItem>
                    <MenuItem value={100000}>100 000</MenuItem>
                </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                    label="Auflösung"
                    value={(data.resolution*1000*1000).toFixed(0)}
                    fullWidth
                    variant="filled"
                    disabled
                    InputProps={{ 
                        readOnly: true,
                        endAdornment: <InputAdornment position="end">µV</InputAdornment>,
                    }}
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                    label="Max. Strombelastung"
                    value={(data.load*1000*1000).toFixed(0)}
                    fullWidth
                    variant="filled"
                    disabled
                    InputProps={{
                        endAdornment: <InputAdornment position="end">µA</InputAdornment>,
                    }}
                    />
            </Grid>
        </Grid>
    );
};

export default EditVoltageForm;
